/**
 * useNavigationGuard - Navigation guard hook to prevent rapid navigation
 * Prevents React Error #185 and other navigation-related issues
 */

import { useRef, useCallback } from 'react'
import { logger } from '../lib/logger'

/**
 * Hook that provides a safe navigation wrapper to prevent rapid navigation
 * and duplicate navigation attempts that can cause React errors
 */
export function useNavigationGuard(cooldownMs = 500) {
  const isNavigatingRef = useRef(false)
  const navigationTimeoutRef = useRef<NodeJS.Timeout>()

  /**
   * Wraps a navigation function to prevent rapid/duplicate calls
   * @param navigationFn The navigation function to execute
   * @returns A promise that resolves when navigation is complete
   */
  const safeNavigate = useCallback(
    async (navigationFn: () => void | Promise<void>) => {
      // Check if already navigating
      if (isNavigatingRef.current) {
        logger.log('[NavigationGuard] Navigation already in progress, ignoring')
        return
      }

      try {
        // Set navigation flag
        isNavigatingRef.current = true

        // Clear any existing timeout
        if (navigationTimeoutRef.current) {
          clearTimeout(navigationTimeoutRef.current)
        }

        // Execute the navigation function
        await navigationFn()

        // Set cooldown period
        navigationTimeoutRef.current = setTimeout(() => {
          isNavigatingRef.current = false
          navigationTimeoutRef.current = undefined
        }, cooldownMs)
      } catch (error) {
        logger.error('[NavigationGuard] Navigation error:', error)
        // Reset immediately on error
        isNavigatingRef.current = false
        throw error
      }
    },
    [cooldownMs]
  )

  /**
   * Check if navigation is currently in progress
   */
  const isNavigating = useCallback(() => {
    return isNavigatingRef.current
  }, [])

  /**
   * Force reset the navigation state (use with caution)
   */
  const resetNavigation = useCallback(() => {
    isNavigatingRef.current = false
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current)
      navigationTimeoutRef.current = undefined
    }
  }, [])

  return {
    safeNavigate,
    isNavigating,
    resetNavigation
  }
}

/**
 * Hook specifically for page navigation with Zustand store
 */
export function usePageNavigation() {
  const { safeNavigate, isNavigating, resetNavigation } = useNavigationGuard()
  
  const navigateToPage = useCallback(
    async (navigateFn: () => void, cleanup?: () => void | Promise<void>) => {
      await safeNavigate(async () => {
        // Execute cleanup if provided
        if (cleanup) {
          await cleanup()
        }
        
        // Small delay to ensure cleanup completes
        await new Promise(resolve => setTimeout(resolve, 50))
        
        // Navigate
        navigateFn()
      })
    },
    [safeNavigate]
  )

  return {
    navigateToPage,
    isNavigating,
    resetNavigation
  }
}